import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Layers,
  Loader2, Clock, Mail as MailIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 3-step RSVP-like signup: Welcome → Intake → Confirmation
export default function HostSignupPage() {
  const { category: urlCat } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [magicToken, setMagicToken] = useState(null);

  const [form, setForm] = useState({
    host_name: "", host_email: "", phone: "", org_name: "",
    location: "", tagline: "", intro: "", hopes: "",
    social_links: { instagram: "", tiktok: "", website: "" },
    enabled_preference_blocks: [],
    uses_acuity: false,
    has_instructor_services: false,
  });

  const category = (urlCat || "makeup").toLowerCase();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // Public fetch via host/category-bundle (no auth needed for read - returns admin fields)
        const r = await fetch(`${API}/host/category-bundle?category_id=${encodeURIComponent(category)}`);
        if (r.ok && !cancel) {
          const d = await r.json();
          setBundle(d);
          // Default: enable all preference blocks except those marked host_toggled
          const enabled = (d.preference_schema || []).filter(b => !b.host_toggled).map(b => b.block_key);
          setForm(f => ({ ...f, enabled_preference_blocks: enabled }));
        }
      } catch {} finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [category]);

  const submit = async () => {
    if (!form.host_name || !form.host_email) { toast.error("Name and email required"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/host/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: category, ...form })
      });
      if (r.ok) {
        const d = await r.json();
        setApplicationId(d.application_id);
        setMagicToken(d.magic_token);
        setStep(3);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Submission failed");
      }
    } catch { toast.error("Network error"); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>;
  if (!bundle) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Category not found.</div>;

  const togglePrefBlock = (bk) => {
    setForm(f => ({
      ...f,
      enabled_preference_blocks: f.enabled_preference_blocks.includes(bk)
        ? f.enabled_preference_blocks.filter(k => k !== bk)
        : [...f.enabled_preference_blocks, bk]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <div className="max-w-xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-3">{bundle.label}</Badge>
          <h1 className="text-3xl font-bold text-gray-900">Become a Phygital host</h1>
          <p className="text-sm text-gray-600 mt-2">Apply for early access. Three quick steps - welcome, intake, confirmation.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="host-signup-steps">
          {["Welcome", "Intake", "Confirm"].map((lbl, i) => (
            <div key={lbl} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= i ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-500"}`}>{i + 1}</div>
              <span className={`text-xs ${step >= i ? "text-purple-700" : "text-gray-400"}`}>{lbl}</span>
              {i < 2 && <div className={`w-6 h-0.5 ${step > i ? "bg-purple-600" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="p-6">
            {/* STEP 0 - Welcome */}
            {step === 0 && (
              <div className="space-y-4" data-testid="host-signup-step-welcome">
                <h2 className="text-xl font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> Welcome</h2>
                <p className="text-sm text-gray-600">Phygital hosts get a plug-and-play dashboard pre-filled from the admin <strong>{bundle.label}</strong> Category Master - preferences, inspo folders, master products, confirmation blocks - so you can start taking bookings fast.</p>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="bg-purple-50 rounded-lg p-3"><p className="text-2xl font-bold text-purple-700">{(bundle.preference_schema || []).reduce((n, b) => n + (b.fields?.length || 0), 0)}</p><p className="text-[11px] text-gray-600">Preference fields</p></div>
                  <div className="bg-purple-50 rounded-lg p-3"><p className="text-2xl font-bold text-purple-700">{(bundle.master_products || []).length}</p><p className="text-[11px] text-gray-600">Master products</p></div>
                </div>
                <Button onClick={() => setStep(1)} className="w-full bg-purple-600 hover:bg-purple-700 mt-2" data-testid="host-signup-welcome-next">
                  Get started <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            )}

            {/* STEP 1 - Intake */}
            {step === 1 && (
              <div className="space-y-3" data-testid="host-signup-step-intake">
                <h2 className="text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-purple-600" /> About you</h2>
                <Input placeholder="Your name *" value={form.host_name} onChange={(e) => setForm({ ...form, host_name: e.target.value })} data-testid="signup-name" />
                <Input placeholder="Email *" type="email" value={form.host_email} onChange={(e) => setForm({ ...form, host_email: e.target.value })} data-testid="signup-email" />
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Input placeholder="Brand / org name" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="signup-org" />
                <Input placeholder="City / location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                <Input placeholder="Tagline (e.g. 'Clean glam, luxury results')" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
                <textarea placeholder="Short intro for your clients" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className="w-full border rounded px-3 py-2 text-sm min-h-[60px]" />

                <div className="space-y-2 pt-2">
                  <p className="text-xs font-semibold text-gray-700">Social</p>
                  <Input placeholder="Instagram @" value={form.social_links.instagram} onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, instagram: e.target.value } })} data-testid="signup-instagram" />
                  <Input placeholder="TikTok @" value={form.social_links.tiktok} onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, tiktok: e.target.value } })} />
                  <Input placeholder="Website" value={form.social_links.website} onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, website: e.target.value } })} />
                </div>

                {/* Preference blocks toggles */}
                <div className="pt-3 border-t space-y-2">
                  <p className="text-xs font-semibold text-gray-700">What you'll collect from clients</p>
                  {(bundle.preference_schema || []).map((b) => (
                    <div key={b.block_key} className="flex items-center justify-between p-2 rounded border" data-testid={`signup-pref-${b.block_key}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{b.block_label}</p>
                        <p className="text-[10px] text-gray-500">{(b.fields || []).map(f => f.label).join(" · ")}</p>
                      </div>
                      <Switch checked={form.enabled_preference_blocks.includes(b.block_key)} onCheckedChange={() => togglePrefBlock(b.block_key)} />
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Uses Acuity Scheduling</span>
                    <Switch checked={form.uses_acuity} onCheckedChange={(v) => setForm({ ...form, uses_acuity: v })} data-testid="signup-acuity" />
                  </div>
                  {bundle.instructor_mode && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Also offers classes / instructor services</span>
                      <Switch checked={form.has_instructor_services} onCheckedChange={(v) => setForm({ ...form, has_instructor_services: v })} data-testid="signup-instructor" />
                    </div>
                  )}
                </div>

                <textarea placeholder="What are you hoping Phygital can help you with? (optional)" value={form.hopes} onChange={(e) => setForm({ ...form, hopes: e.target.value })} className="w-full border rounded px-3 py-2 text-sm min-h-[50px] mt-2" />

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(0)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
                  <Button onClick={() => setStep(2)} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="host-signup-intake-next">
                    Review <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2 - Review & submit */}
            {step === 2 && (
              <div className="space-y-3" data-testid="host-signup-step-review">
                <h2 className="text-xl font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-purple-600" /> Review</h2>
                <div className="space-y-1 text-sm bg-gray-50 rounded-lg p-3">
                  <p><strong>Name:</strong> {form.host_name}</p>
                  <p><strong>Email:</strong> {form.host_email}</p>
                  <p><strong>Category:</strong> {bundle.label}</p>
                  {form.org_name && <p><strong>Brand:</strong> {form.org_name}</p>}
                  {form.location && <p><strong>Location:</strong> {form.location}</p>}
                  <p><strong>Collecting:</strong> {form.enabled_preference_blocks.join(", ") || "none"}</p>
                  {form.uses_acuity && <p className="text-purple-700">✓ Uses Acuity</p>}
                  {form.has_instructor_services && <p className="text-purple-700">✓ Offers classes / instructor services</p>}
                </div>
                <p className="text-xs text-gray-500">On submission, admin will review your application (usually within 24h). You'll see a waiting card on <code>/me</code> until approved.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
                  <Button onClick={submit} disabled={submitting} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="host-signup-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3 - Confirmation (submitted) */}
            {step === 3 && (
              <div className="space-y-3 text-center" data-testid="host-signup-step-confirm">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Clock className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">Application submitted</h2>
                <p className="text-sm text-gray-600">Admin will review your application and activate your dashboard. You'll receive an email when approved.</p>
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-left text-xs">
                  <p className="font-semibold text-purple-900 mb-1">Your application id</p>
                  <code className="text-purple-700">{applicationId}</code>
                </div>
                {magicToken && (
                  <Button onClick={() => navigate(`/auth/magic?token=${magicToken}`)} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="host-signup-view-status">
                    <MailIcon className="w-4 h-4 mr-1.5" /> View my waiting card
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
